// Exportación del RESUMEN de la liquidación a repartidores: a diferencia de la planilla
// banco (solo quienes cobran, formato Santander), acá sale TODO lo que muestra la vista
// —incluidos los que no subieron documento o están pendientes de validar— para poder
// perseguir faltantes fuera de la app. Dos hojas: "Resumen" (una fila por repartidor,
// con fila de totales) y "Días" (una fila por repartidor-día).
import { utils, writeFile } from 'xlsx';
import { driverVehicleLabel } from '../../../../utils/driverVehicleTypes';
import { billingDocLabel } from '../../../../utils/billingDocTypes';

// Anchos (en caracteres) para que las dos hojas se lean sin reacomodar columnas.
const SUMMARY_COL_WIDTHS = [28, 26, 30, 14, 16, 22, 9, 12, 10, 16, 12, 12, 12, 12, 14, 12, 12, 12, 14, 28, 14, 14, 14, 26, 14, 18, 14, 16, 16];

// RUT normalizado para COMPARAR (solo dígitos + K mayúscula): mismo criterio que usa el
// backend para el control de duplicados del documento.
const normRut = (s) => String(s ?? '').replace(/[^0-9kK]/g, '').toUpperCase();
const DAYS_COL_WIDTHS = [28, 12, 12, 10, 10, 12, 12, 12, 12, 14];

// "Retiros" y "Retiros (neto)" van justo después de los sobredimensionados.
const PICKUP_COL_WIDTHS = [9, 14];
const SUMMARY_PICKUP_COL = 10;
const DAYS_PICKUP_COL = 5;

const money = (v) => Math.round(Number(v) || 0);

// Las columnas de retiros solo existen si el período paga retiros ('ok') o debía pagarlos
// y billing no pudo leerlos ('unavailable'). Con 'disabled' o sin el campo (billing sin
// desplegar) no se agregan: dos columnas en 0 correrían de lugar todas las siguientes en
// el archivo de quien no usa la opción (fórmulas o macros por posición).
const pickupColumnsShown = (pickupsStatus) =>
  pickupsStatus === 'ok' || pickupsStatus === 'unavailable';

const pickupHeaders = (pickupsStatus, t) =>
  pickupColumnsShown(pickupsStatus) ? [t('Retiros'), t('Retiros (neto)')] : [];

// Retiros realizados (cantidad y neto). Con 'unavailable' las celdas quedan VACÍAS,
// porque un 0 afirmaría que no hubo retiros (misma regla que dayMoney cuando falta
// baseOwed: no se inventa). Con 'ok', un campo ausente es un repartidor o día sin retiros.
const pickupCells = (row, pickupsStatus) => {
  if (!pickupColumnsShown(pickupsStatus)) return [];
  if (pickupsStatus === 'unavailable') return [null, null];
  return [Number(row?.pickupsCount ?? 0), money(row?.pickupsNet)];
};

// Anchos con las columnas de retiros en su lugar, cuando corresponden.
const withPickupWidths = (widths, at, pickupsStatus) =>
  pickupColumnsShown(pickupsStatus)
    ? [...widths.slice(0, at), ...PICKUP_COL_WIDTHS, ...widths.slice(at)]
    : widths;

/**
 * Estado del documento tributario en texto plano. Misma lógica que el badge de la vista
 * (DocStatusBadge): freelancer no aplica; semana en curso no es "falta" (todavía no
 * puede subirlo); rechazado pisa a todo lo demás.
 */
export function docStatusLabel(d, weekOpen, t) {
  if (d.isFreelancer || d.billingDocStatus == null) return '—';
  if (d.billingDocStatus === 'accepted') return t('Aceptado');
  if (d.billingDocStatus === 'uploaded') return t('Subido (por validar)');
  if (d.billingDocStatus === 'rejected') return t('Rechazado');
  if (weekOpen) return t('Semana en curso');
  return t('Falta su documento');
}

// Desglose de dinero de un repartidor: misma cadena auditable de la vista
// (Subtotal + Extras = Neto · Neto + IVA = Total a cobrar; el IVA se deriva).
// totalNet ya incluye los retiros realizados: "Retiros (neto)" es la parte del Subtotal
// que les corresponde, no un monto que se sume aparte.
const driverMoney = (d) => {
  const subtotal = d.totalNet ?? 0;
  const extras = d.extrasTotal ?? 0;
  const neto = subtotal + extras;
  const total = d.totalPayable ?? 0;
  return { subtotal, extras, neto, iva: total - neto, total };
};

// Desglose de un día (misma regla que dayBreakdown de la vista): si el backend no
// expone baseOwed, no se inventa el desglose (celdas vacías, solo el total).
const dayMoney = (day) => {
  const extras = day.extrasTotal ?? 0;
  const total = day.totalPayable ?? 0;
  if (day.baseOwed == null) {
    return { subtotal: null, extras, neto: null, iva: null, total };
  }
  return {
    subtotal: day.baseOwed - extras,
    extras,
    neto: day.baseOwed,
    iva: total - day.baseOwed,
    total,
  };
};

// Tipo de documento en texto: los freelancers no eligen documento (no se les paga por
// planilla), así que su "Sin documento" mentiría; se rotulan como Freelancer. Con semanas
// de tipos distintos (cada una se paga con el suyo) no hay un tipo único que poner.
const docTypeLabel = (d, t) =>
  d.mixedDocTypes === true
    ? t('Semanas con tipos distintos')
    : d.billingDocType
      ? billingDocLabel(d.billingDocType, t)
      : d.isFreelancer
        ? t('Freelancer')
        : t('Sin documento');

/** Filas (AOA) de la hoja "Resumen": encabezado + una por repartidor + totales.
 * Junto al nombre va el CORREO del repartidor (`getEmail(driverId)`, mismo catálogo de
 * usuarios que los datos bancarios): el resumen se usa para perseguir faltantes fuera
 * de la app, y sin correo hay que ir a buscarlo a otra vista.
 * Al final van los datos IMPRESOS registrados al aceptar (docMeta del documento más
 * reciente): folio, RUT y nombre del emisor, fechas y monto del documento. Vacíos si
 * nadie los registró (documentos aceptados antes de que existiera el formulario).
 * Y después, los datos BANCARIOS del repartidor (`getBank(driverId)` → bankData) con la
 * columna "¿Emisor = titular?": compara RUT del emisor del documento contra RUT del
 * titular de la cuenta, que es el cruce que interesa (a quién se transfiere vs quién
 * facturó). SÍ/NO; vacía si falta cualquiera de los dos RUT. */
export function buildSummaryRows({ drivers = [], totals, weekOpen, pickupsStatus, getName, getEmail, getBank, t }) {
  const header = [
    t('Repartidor'),
    t('ID'),
    t('Correo'),
    t('Vehículo'),
    t('Tipo de documento'),
    t('Estado del documento'),
    t('Días'),
    t('Días pagados'),
    t('Entregas'),
    t('Sobredimensionados'),
    ...pickupHeaders(pickupsStatus, t),
    t('Subtotal'),
    t('Extras'),
    t('Neto'),
    t('IVA'),
    t('Total a cobrar'),
    t('Retenido'),
    t('A pagar'),
    t('N° documento'),
    t('RUT emisor'),
    t('Emisor del documento'),
    t('Fecha del documento'),
    t('Fecha de emisión'),
    t('Monto del documento'),
    t('Titular de la cuenta'),
    t('RUT titular'),
    t('Banco'),
    t('Tipo de cuenta'),
    t('N° de cuenta'),
    t('¿Emisor = titular?'),
  ];
  const rows = drivers.map((d) => {
    const m = driverMoney(d);
    // Datos registrados del documento más reciente que los tenga.
    const meta = (d.billingDocuments ?? []).map((doc) => doc?.docMeta).find(Boolean) ?? {};
    const bank = getBank?.(d.driverId) ?? {};
    const rutEmisor = normRut(meta.emisorRut);
    const rutTitular = normRut(bank.rut);
    const cruce = rutEmisor && rutTitular ? (rutEmisor === rutTitular ? t('SÍ') : t('NO')) : '';
    return [
      getName?.(d.driverId) || d.driverId,
      d.driverId,
      getEmail?.(d.driverId) ?? '',
      d.vehicleType ? driverVehicleLabel(d.vehicleType, t) : t('Sin vehículo'),
      docTypeLabel(d, t),
      docStatusLabel(d, weekOpen, t),
      d.daysCount ?? 0,
      d.paidDaysCount ?? 0,
      d.deliveriesCount ?? 0,
      d.oversizedCount ?? 0,
      ...pickupCells(d, pickupsStatus),
      money(m.subtotal),
      money(m.extras),
      money(m.neto),
      // Semanas de tipos distintos: total − neto mezcla IVA (factura) con retención
      // (boleta); la celda queda vacía en vez de una cifra que no es ninguna de las dos.
      d.mixedDocTypes === true ? null : money(m.iva),
      money(m.total),
      money(d.withheldAmount),
      money(d.payableTotal),
      meta.folio ?? '',
      meta.emisorRut ?? '',
      meta.emisorNombre ?? '',
      meta.fechaDocumento ?? '',
      meta.fechaEmision ?? '',
      meta.montoTotal != null ? money(meta.montoTotal) : '',
      bank.name ?? '',
      bank.rut ?? '',
      bank.bank ?? '',
      bank.accountType ?? '',
      // La cuenta va como TEXTO: un cero inicial perdido es una transferencia errada.
      bank.accountNumber != null ? String(bank.accountNumber) : '',
      cruce,
    ];
  });
  const totalRow = totals
    ? [
        t('TOTALES'),
        '',
        '',
        '',
        '',
        '',
        null,
        null,
        totals.deliveriesCount ?? 0,
        totals.oversizedCount ?? 0,
        ...pickupCells(totals, pickupsStatus),
        money(totals.totalNet),
        money(totals.extrasTotal),
        money((totals.totalNet ?? 0) + (totals.extrasTotal ?? 0)),
        money(totals.taxAmount),
        money(totals.totalPayable),
        money(totals.withheldAmount),
        money(totals.payableTotal),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ]
    : null;
  return totalRow ? [header, ...rows, totalRow] : [header, ...rows];
}

const DAY_STATUS_LABEL = { paid: 'Pagado', partial: 'Parcial', unpaid: 'No pagado' };

/** Filas (AOA) de la hoja "Días": encabezado + una por (repartidor, día). */
export function buildDayRows({ drivers = [], pickupsStatus, getName, t }) {
  const header = [
    t('Repartidor'),
    t('Fecha'),
    t('Estado de pago'),
    t('Entregas'),
    t('Sobredim.'),
    ...pickupHeaders(pickupsStatus, t),
    t('Subtotal'),
    t('Extras'),
    t('Neto'),
    t('IVA'),
    t('Total a cobrar'),
  ];
  const rows = [];
  for (const d of drivers) {
    const name = getName?.(d.driverId) || d.driverId;
    for (const day of d.days ?? []) {
      const m = dayMoney(day);
      rows.push([
        name,
        day.date,
        t(DAY_STATUS_LABEL[day.status] ?? 'No pagado'),
        day.deliveriesCount ?? 0,
        day.oversizedCount ?? 0,
        ...pickupCells(day, pickupsStatus),
        m.subtotal == null ? null : money(m.subtotal),
        money(m.extras),
        m.neto == null ? null : money(m.neto),
        m.iva == null ? null : money(m.iva),
        money(m.total),
      ]);
    }
  }
  return [header, ...rows];
}

// Workbook con las dos hojas. Puro y testeable (no descarga nada).
export function buildPayrollSummaryWorkbook(input) {
  const wb = utils.book_new();
  const wsSummary = utils.aoa_to_sheet(buildSummaryRows(input));
  wsSummary['!cols'] = withPickupWidths(SUMMARY_COL_WIDTHS, SUMMARY_PICKUP_COL, input.pickupsStatus)
    .map((wch) => ({ wch }));
  utils.book_append_sheet(wb, wsSummary, 'Resumen');
  const wsDays = utils.aoa_to_sheet(buildDayRows(input));
  wsDays['!cols'] = withPickupWidths(DAYS_COL_WIDTHS, DAYS_PICKUP_COL, input.pickupsStatus)
    .map((wch) => ({ wch }));
  utils.book_append_sheet(wb, wsDays, 'Días');
  return wb;
}

export const payrollSummaryFilename = (from, to) =>
  `resumen-pago-repartidores-${from}-a-${to}.xlsx`;

// Construye y dispara la descarga del .xlsx en el navegador.
export function downloadPayrollSummarySheet(input) {
  const wb = buildPayrollSummaryWorkbook(input);
  writeFile(wb, payrollSummaryFilename(input.from, input.to));
}
